import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import SpikesGearHeader from './SpikesGearHeader';
import './OrderForm.css';

const SIZES = ['YS', 'YM', 'YL', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
const BASE_SHIRT_PRICE = 8.99;
const NAME_PRINT_FEE = 10.0;
const PRICE_PER_SHIRT = BASE_SHIRT_PRICE + NAME_PRINT_FEE;

const emptyItem = () => ({ name_on_back: '', size: '', quantity: 1 });

const OrderForm = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [salespersonId, setSalespersonId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([emptyItem()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [notesCharCount, setNotesCharCount] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/spikes-gear/users/active');
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const addItem = () => {
    setItems((prev) => [...prev, emptyItem()]);
  };

  const removeItem = (index) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const totalShirts = items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0);
  const totalPrice = totalShirts * PRICE_PER_SHIRT;

  const validateForm = () => {
    const newErrors = {};

    if (!customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    } else if (customerName.length > 255) {
      newErrors.customerName = 'Customer name cannot exceed 255 characters';
    }

    if (!customerPhone || customerPhone.replace(/\D/g, '').length !== 10) {
      newErrors.customerPhone = 'A valid 10-digit phone number is required';
    }

    const itemErrors = items.map((item) => {
      if (!item.name_on_back.trim() || !item.size || !item.quantity || item.quantity <= 0) {
        return 'Each shirt needs a name, size, and quantity greater than 0';
      }
      return null;
    });
    if (itemErrors.some(Boolean)) {
      newErrors.items = 'Every shirt line needs a name to print on back, a size, and a quantity greater than 0';
    }

    if (notes && notes.length > 1000) {
      newErrors.notes = 'Notes cannot exceed 1000 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orderData = {
        customer_name: customerName,
        customer_phone: customerPhone,
        items: items.map((i) => ({
          name_on_back: i.name_on_back.trim(),
          size: i.size,
          quantity: parseInt(i.quantity),
        })),
        salesperson_id: salespersonId || null,
        notes,
      };

      await api.post('/spikes-gear/orders', orderData);
      setSuccess(true);

      // Reset form
      setCustomerName('');
      setCustomerPhone('');
      setSalespersonId('');
      setNotes('');
      setItems([emptyItem()]);
      setNotesCharCount(0);
      setErrors({});

      setTimeout(() => {
        window.location.href = '/spikes-gear';
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-form-container">
      <SpikesGearHeader />
      <div className="form-header">
        <h1>Create New Spikes Gear Order</h1>
        <p>Fill in the details below to create a new t-shirt order</p>
      </div>

      {success && (
        <div className="success-message">
          ✓ Order created successfully! Redirecting to dashboard...
        </div>
      )}

      {error && <div className="error-message">✕ {error}</div>}

      <form onSubmit={handleSubmit} className="order-form">
        {/* CUSTOMER INFO */}
        <div className="form-section">
          <h3>Customer Info</h3>
          <p className="form-section-hint">Who placed the order — used to follow up once it's ready.</p>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer_name">
                Customer Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="customer_name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                maxLength="255"
                placeholder="Enter customer name"
                className={errors.customerName ? 'input-error' : ''}
              />
              {errors.customerName && <span className="error-text">{errors.customerName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="customer_phone">
                Customer Phone <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="customer_phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(formatPhoneNumber(e.target.value))}
                placeholder="123-456-7890"
                className={errors.customerPhone ? 'input-error' : ''}
              />
              {errors.customerPhone && <span className="error-text">{errors.customerPhone}</span>}
            </div>
          </div>
        </div>

        {/* ORDER DETAILS */}
        <div className="form-section">
          <h3>Order Details</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Price per Shirt</label>
              <p className="form-section-hint" style={{ margin: 0 }}>
                ${PRICE_PER_SHIRT.toFixed(2)} fixed (${BASE_SHIRT_PRICE.toFixed(2)} shirt + ${NAME_PRINT_FEE.toFixed(2)} name print)
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="salesperson_id">Salesperson</label>
              <select
                id="salesperson_id"
                value={salespersonId}
                onChange={(e) => setSalespersonId(e.target.value)}
              >
                <option value="">-- Select Salesperson --</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name || u.username}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* SHIRTS ROSTER */}
          <div className="items-section">
            <div className="items-header">
              <label>
                Shirts <span className="required">*</span>
              </label>
              <span className="items-summary">
                {totalShirts} shirt{totalShirts === 1 ? '' : 's'} · ${totalPrice.toFixed(2)} total
              </span>
            </div>
            {errors.items && <span className="error-text">{errors.items}</span>}

            <div className="items-table">
              <div className="items-table-header">
                <span>Name on Back</span>
                <span>Size</span>
                <span>Qty</span>
                <span></span>
              </div>
              {items.map((item, index) => (
                <div className="items-table-row" key={index}>
                  <input
                    type="text"
                    placeholder="Name to print on back"
                    value={item.name_on_back}
                    onChange={(e) => handleItemChange(index, 'name_on_back', e.target.value)}
                    maxLength="100"
                  />
                  <select
                    value={item.size}
                    onChange={(e) => handleItemChange(index, 'size', e.target.value)}
                  >
                    <option value="">Size</option>
                    {SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-remove-item"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    title="Remove shirt"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <button type="button" className="btn-add-item" onClick={addItem}>
              + Add Another Shirt
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="notes">
              Notes <span className="char-count">({notesCharCount}/1000)</span>
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesCharCount(e.target.value.length);
              }}
              maxLength="1000"
              placeholder="Any special instructions or notes"
              rows="4"
              className={errors.notes ? 'input-error' : ''}
            ></textarea>
            {errors.notes && <span className="error-text">{errors.notes}</span>}
          </div>
        </div>

        {/* FORM ACTIONS */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => (window.location.href = '/spikes-gear')}
            className="btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating Order...' : 'Create Order'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OrderForm;
